import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Clock, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MultiSelect } from '@/components/common/MultiSelect';
import { Clinic, Facility } from '@/services/features/appointmentSlotsService.ts';

interface CreateTimeSlotsDialogComponentProps {
  isOpen: boolean;
  clinics: Clinic[];
  dialogFacilities: Facility[];
  dialogClinic: string;
  selectedDialogFacility: string;
  selectedDays: string[];
  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
  duration: string;
  clinicSearchQuery: string;
  dialogFacilitySearchQuery: string;
  validationErrors: Record<string, string>;
  isLoadingDialogFacilities: boolean;
  isCreatingSlots: boolean;
  onOpenChange: (open: boolean) => void;
  onDialogClinicChange: (clinicName: string) => void;
  onDialogFacilityChange: (facilityName: string) => void;
  onSelectedDaysChange: (days: string[]) => void;
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
  onStartTimeChange: (time: string) => void;
  onEndTimeChange: (time: string) => void;
  onDurationChange: (duration: string) => void;
  onClinicSearchChange: (query: string) => void;
  onDialogFacilitySearchChange: (query: string) => void;
  onValidationErrorsClear: (field: string) => void;
  onConfirm: () => void;
}

const CreateTimeSlotsDialogComponent: React.FC<CreateTimeSlotsDialogComponentProps> = ({
  isOpen,
  clinics,
  dialogFacilities,
  dialogClinic,
  selectedDialogFacility,
  selectedDays,
  startDate,
  endDate,
  startTime,
  endTime,
  duration,
  clinicSearchQuery,
  dialogFacilitySearchQuery,
  validationErrors,
  isLoadingDialogFacilities,
  isCreatingSlots,
  onOpenChange,
  onDialogClinicChange,
  onDialogFacilityChange,
  onSelectedDaysChange,
  onStartDateChange,
  onEndDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onDurationChange,
  onClinicSearchChange,
  onDialogFacilitySearchChange,
  onValidationErrorsClear,
  onConfirm,
}) => {
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  // Check if all required fields are filled
  const isFormComplete = 
    dialogClinic &&
    selectedDialogFacility &&
    selectedDays.length > 0 &&
    startDate &&
    endDate &&
    startTime &&
    endTime &&
    duration;

  // Filter clinics for dialog search
  const filteredClinicsForDialog = clinics.filter(clinic =>
    clinic.clinic_name.toLowerCase().includes(clinicSearchQuery.toLowerCase())
  );

  const handleStartDateChange = (date: Date | undefined) => {
    if (date) {
      onStartDateChange(date);
      // Clear start date validation error if it exists
      if (validationErrors.startDate) {
        onValidationErrorsClear('startDate');
      }
      // Clear start time validation error since date change might affect time validation
      if (validationErrors.startTime) {
        onValidationErrorsClear('startTime');
      }
      // If end date is selected and is before the new start date, reset it
      if (endDate && new Date(endDate) < date) {
        onEndDateChange(undefined);
        // Clear end date validation error if it exists
        if (validationErrors.endDate) {
          onValidationErrorsClear('endDate');
        }
      }
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (date) {
      onEndDateChange(date);
      // Clear end date validation error if it exists
      if (validationErrors.endDate) {
        onValidationErrorsClear('endDate');
      }
      // Clear end time validation error since date change might affect time validation
      if (validationErrors.endTime) {
        onValidationErrorsClear('endTime');
      }
    }
  };

  const handleStartTimeChange = (time: string) => {
    onStartTimeChange(time);
    // Clear start time validation error if it exists
    if (validationErrors.startTime) {
      onValidationErrorsClear('startTime');
    }
  };

  const handleEndTimeChange = (time: string) => {
    onEndTimeChange(time);
    // Clear end time validation error if it exists
    if (validationErrors.endTime) {
      onValidationErrorsClear('endTime');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Time Slots</DialogTitle>
          <DialogDescription>
            Set up recurring appointment slots for a clinic
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Clinic Selection with Search */}
          <div className="space-y-2">
            <Label>Select Clinic *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={`w-full justify-start ${validationErrors.clinic ? 'border-red-500' : ''}`}>
                  {dialogClinic || "Select Clinic"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <div className="p-4">
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search..."
                      value={clinicSearchQuery}
                      onChange={(e) => onClinicSearchChange(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {filteredClinicsForDialog.map((clinic) => (
                      <div
                        key={clinic.clinic_id}
                        className="p-2 hover:bg-gray-100 cursor-pointer rounded"
                        onClick={() => {
                          onDialogClinicChange(clinic.clinic_name);
                        }}
                      >
                        {clinic.clinic_name}
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            {validationErrors.clinic && (
              <p className="text-sm text-red-500">{validationErrors.clinic}</p>
            )}
          </div>

          {/* Facility Selection with Search */}
          {dialogClinic && (
            <div className="space-y-2">
              <Label>Select Facility Name *</Label>
              <Select value={selectedDialogFacility} onValueChange={onDialogFacilityChange}>
                <SelectTrigger className={`w-full ${validationErrors.facility ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="Select Facility" />
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2">
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search facilities..."
                        value={dialogFacilitySearchQuery}
                        onChange={(e) => onDialogFacilitySearchChange(e.target.value)}
                        className="pl-10 h-8"
                      />
                    </div>
                  </div>
                  {isLoadingDialogFacilities ? (
                    <SelectItem value="loading" disabled>Loading facilities...</SelectItem>
                  ) : dialogFacilities.length === 0 ? (
                    <SelectItem value="no-data" disabled>No facilities found</SelectItem>
                  ) : (
                    dialogFacilities
                      .filter(facility => 
                        facility.facility_name.toLowerCase().includes(dialogFacilitySearchQuery.toLowerCase())
                      )
                      .map((facility) => (
                        <SelectItem key={facility.facility_id} value={facility.facility_name}>
                          {facility.facility_name}
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
              {validationErrors.facility && (
                <p className="text-sm text-red-500">{validationErrors.facility}</p>
              )}
            </div>
          )}

          {/* Days of Week Multi-Select */}
          <div className="space-y-2">
            <Label>Select Days of Week *</Label>
            <MultiSelect
              options={daysOfWeek.map(day => ({ label: day, value: day }))}
              value={selectedDays}
              onChange={(value) => onSelectedDaysChange(value as string[])}
              placeholder="Select Days of Week"
              className={validationErrors.days ? 'border-red-500' : ''}
            />
            {validationErrors.days && (
              <p className="text-sm text-red-500">{validationErrors.days}</p>
            )}
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={`w-full justify-start ${validationErrors.startDate ? 'border-red-500' : ''}`}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? startDate.toLocaleDateString() : "Start Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={handleStartDateChange}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const checkDate = new Date(date);
                      checkDate.setHours(0, 0, 0, 0);
                      return checkDate < today;
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              {validationErrors.startDate && (
                <p className="text-sm text-red-500">{validationErrors.startDate}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>End Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={`w-full justify-start ${validationErrors.endDate ? 'border-red-500' : ''}`}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? endDate.toLocaleDateString() : "End Date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={handleEndDateChange}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const checkDate = new Date(date);
                      checkDate.setHours(0, 0, 0, 0);
                      
                      // Disable dates before today
                      if (checkDate < today) return true;
                      
                      // If start date is selected, disable dates before start date
                      if (startDate) {
                        const startDateCheck = new Date(startDate);
                        startDateCheck.setHours(0, 0, 0, 0);
                        return checkDate < startDateCheck;
                      }
                      
                      return false;
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              {validationErrors.endDate && (
                <p className="text-sm text-red-500">{validationErrors.endDate}</p>
              )}
            </div>
          </div>

          {/* Time Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Time *</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                  className={`pl-10 ${validationErrors.startTime ? 'border-red-500' : ''}`}
                />
              </div>
              {validationErrors.startTime && (
                <p className="text-sm text-red-500">{validationErrors.startTime}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>End Time *</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => handleEndTimeChange(e.target.value)}
                  className={`pl-10 ${validationErrors.endTime ? 'border-red-500' : ''}`}
                />
              </div>
              {validationErrors.endTime && (
                <p className="text-sm text-red-500">{validationErrors.endTime}</p>
              )}
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label>Enter duration for appointment slots (mins) *</Label>
            <Input
              type="number"
              placeholder="15"
              value={duration}
              onChange={(e) => onDurationChange(e.target.value)}
              className={validationErrors.duration ? 'border-red-500' : ''}
            />
            {validationErrors.duration && (
              <p className="text-sm text-red-500">{validationErrors.duration}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="px-8"
            disabled={isCreatingSlots}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-8"
            disabled={!isFormComplete || isCreatingSlots}
          >
            {isCreatingSlots ? "Creating..." : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTimeSlotsDialogComponent;